import qs from 'query-string';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import NavigateLink from '../../components/NavigateLink';
import { createCustomCell } from '../../components/SavedSearchesTable/utils';
import { ACTOR_TYPES } from '../../constants/Common';
import {
  ENTITY_DISPLAY,
  ENTITY_EDITOR,
  FORM_EDITOR,
  REPORT_DESIGNER,
  REPORT_MODEL_DESIGNER,
  REPOSITORY_CONFIGURATION,
  SAVED_SEARCH_EDITOR,
  SMART_INSIGHT_REPORTS,
  SMART_INSIGHT_REPORTS_LIST,
  SMART_INSIGHT_SAVED_SEARCHES,
} from '../../constants/Urls';
import { getToken, getTranslations } from '../../form-designer/selectors/common.selectors';
import { isExternalLink } from '../../form-designer/utils/controls/ssHyperLink.utils';
import { getSmartInsightApplicationURL } from '../../selectors/AuthSelectors';
import { decodeString } from '../../utils/commonUtils';
import { getEnvVar } from '../../utils/env';
import { mapUrlParams, parseNavigationColumnParams } from '../../utils/legacyRouteMapper';
import { getLinkToLegacyReportViewer } from '../../utils/smartInsightUtils';

const getNavigationColumnParams = (row) => row?.NavigationColumnParams ?? '';

const getNavigationColumnLink = (row) => {
  const params = getNavigationColumnParams(row);
  const str = decodeString(params);
  return /^\//.test(str) ? str : '/' + str;
};

const isInternalRoute = (row) => {
  const params = getNavigationColumnParams(row);
  return /^(?!.*\.aspx).*$/.test(params);
};

const createEntityDisplayParams = (row) =>
  qs.stringify(mapUrlParams(row, { ignoreUnknownParams: true }));

const createEntityDisplayLink = (row, pathname) => {
  return isInternalRoute(row)
    ? getNavigationColumnLink(row)
    : {
        pathname,
        search: createEntityDisplayParams(row),
      };
};

function NavigateLinkCell({ row, column, link, target, disabled }) {
  const translations = useSelector(getTranslations);
  const location = useLocation();
  const qsParams = qs.parse(location.search) || {};
  const isTargetBlank = qsParams.targetBlank === '0';

  return (
    <>
      {disabled ? (
        createCustomCell(column, translations)({ row })
      ) : (
        <NavigateLink to={link} target={isTargetBlank ? '_blank' : target} row={row}>
          {createCustomCell(column, translations)({ row })}
        </NavigateLink>
      )}
    </>
  );
}

NavigateLinkCell.forUrl = (pathname, target) => (column) => ({ row }) => {
  const token = useSelector(getToken);
  const smartInsightApplicationURL = useSelector(getSmartInsightApplicationURL);

  if (RegExp(`${ENTITY_DISPLAY}$`, 'i').test(pathname)) {
    const isSmartRecord = RegExp('V2SmartReport.aspx', 'i').test(row.NavigationColumnParams);
    const link = isSmartRecord
      ? parseNavigationColumnParams(row.NavigationColumnParams)
      : createEntityDisplayLink(row, pathname);

    const props = {
      column,
      row,
      target: isSmartRecord ? '_blank' : target,
      link,
    };

    return <NavigateLinkCell {...props} />;
  }

  if (
    [FORM_EDITOR, ENTITY_EDITOR, SAVED_SEARCH_EDITOR, REPORT_MODEL_DESIGNER].some((editor) =>
      new RegExp(`${editor}$`, 'i').test(pathname)
    )
  ) {
    const isSavedSearchEditorLink = RegExp(`${SAVED_SEARCH_EDITOR}$`, 'i').test(pathname);

    const navigationLink = isSavedSearchEditorLink
      ? `${getNavigationColumnLink(row)}&entityName=${row.EntityName}`
      : getNavigationColumnLink(row);

    const props = {
      column,
      row,
      link: `${navigationLink}&standaloneLayout=true`,
    };

    return <NavigateLinkCell {...props} />;
  }

  if (SMART_INSIGHT_SAVED_SEARCHES === pathname) {
    const navigationLink = getNavigationColumnLink(row);

    const props = {
      column,
      row,
      target: '_blank',
      link: `${getEnvVar('REACT_APP_ADMIN_APP_URL')}${navigationLink}&Token=${token}`,
    };

    return <NavigateLinkCell {...props} />;
  }

  if (SMART_INSIGHT_REPORTS === pathname) {
    const reportId = row.QYD_ID;

    const props = {
      column,
      row,
      link: `${REPORT_DESIGNER}?reportId=${reportId}&standaloneLayout=true`,
    };

    return <NavigateLinkCell {...props} />;
  }

  if (SMART_INSIGHT_REPORTS_LIST === pathname) {
    const reportId = row.QYD_ID;

    const props = {
      column,
      row,
      link: getLinkToLegacyReportViewer(smartInsightApplicationURL, reportId, token),
      target,
    };

    return <NavigateLinkCell {...props} />;
  }

  if (REPOSITORY_CONFIGURATION === pathname) {
    const repositoryId = row.QYD_ID;

    const props = {
      column,
      row,
      link: `${REPOSITORY_CONFIGURATION}?repositoryId=${repositoryId}`,
    };

    return <NavigateLinkCell {...props} />;
  }

  return null;
};

NavigateLinkCell.fromNavigationColumn = (column) => ({ row }) => {
  if (!row.NavigationColumnParams) {
    return NavigateLinkCell.forUrl(ENTITY_DISPLAY)(column)({ row });
  }

  const link = parseNavigationColumnParams(row.NavigationColumnParams);

  let target = null;

  if (isExternalLink(link.pathname)) {
    target = '_blank';
  }

  const props = { row, column, link, target };

  return <NavigateLinkCell {...props} />;
};

NavigateLinkCell.savedSearchColumnLink = (column) => ({ row }) => {
  if (!row.NavigationColumnParams) {
    return <NavigateLinkCell disabled column={column} row={row} />;
  }

  const link = parseNavigationColumnParams(row.NavigationColumnParams);
  const target = '_blank';
  const props = { row, column, link, target };

  return <NavigateLinkCell {...props} />;
};

NavigateLinkCell.roleActorsManagementLink = (column) => ({ row = {} }) => {
  const token = useSelector(getToken);
  const {
    RSR_CLASS_UNTRANSLATED,
    ACT_ID,
    ItmId,
    ACT_SITE_ID,
    RSR_ORG_ID,
    NavigationColumnParams,
  } = row;

  const actorId = ACT_ID || ItmId;
  const link =
    RSR_CLASS_UNTRANSLATED === ACTOR_TYPES.INTERNAL_ACTOR
      ? `${ENTITY_DISPLAY}?itmId=${actorId}&entityType=IAC_INTERNAL_ACTOR&formKey=IAC_INTERNAL_ACTOR_DETAIL`
      : RSR_CLASS_UNTRANSLATED === ACTOR_TYPES.PERSON
      ? `${ENTITY_DISPLAY}?itmId=${actorId}&entityType=PSN_PERSON&formKey=PSN_PERSON_DETAIL`
      : RSR_CLASS_UNTRANSLATED === ACTOR_TYPES.CONTACT
      ? `${getEnvVar(
          'REACT_APP_ADMIN_APP_URL'
        )}/CTN_CONTACT_MAIN.aspx?ParID=${ACT_SITE_ID}&HdrID=&ItmID=${ACT_ID}&NavAction=Detail&Path=&ShowAll=False&PortalSelectedOrgId=${RSR_ORG_ID}&Token=${token}`
      : parseNavigationColumnParams(NavigationColumnParams);

  const props = { row, column, link, target: '_blank' };

  return <NavigateLinkCell {...props} />;
};

export default NavigateLinkCell;
